
import jsPDF from 'jspdf';
import { TranslationValue } from '../i18n/i18n';

interface ExportData {
  initialInvestment: number;
  monthlyContribution: number;
  interestRate: number;
  years: number;
  compoundFrequency: number;
  totalContributions: number;
  interestEarned: number;
  finalBalance: number;
  yearlyData: Array<{
    year: number;
    principal: number;
    balance: number;
  }>;
}

interface TranslationFunction {
  (key: string): string;
}

export const generateCompoundInterestPDF = (
  data: ExportData,
  currency: string,
  t: TranslationFunction
) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  const margin = 20;
  let yPosition = 30;

  // Helper function to format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat(currency === "USD" ? "en-US" : "de-DE", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // Helper function to get frequency text
  const getFrequencyText = (frequency: number) => {
    switch (frequency) {
      case 1: return t("tools.compound.annually");
      case 2: return t("tools.compound.semiAnnually");
      case 4: return t("tools.compound.quarterly");
      case 12: return t("tools.compound.monthly");
      case 365: return t("tools.compound.daily");
      default: return frequency.toString();
    }
  };

  // Title
  doc.setFontSize(20);
  doc.setFont("helvetica", "bold");
  doc.text(t("tools.compound.title"), pageWidth / 2, yPosition, { align: "center" });
  yPosition += 20;

  // Input Parameters Section
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text(t("calculator.enterDetails"), margin, yPosition);
  yPosition += 15;

  doc.setFontSize(12);
  doc.setFont("helvetica", "normal");
  
  const inputData = [
    [t("tools.compound.initialInvestment"), formatCurrency(data.initialInvestment)],
    [t("tools.compound.monthlyContribution"), formatCurrency(data.monthlyContribution)],
    [t("tools.compound.interestRate"), `${data.interestRate}%`],
    [t("tools.compound.frequency"), getFrequencyText(data.compoundFrequency)],
    [t("tools.compound.years"), data.years.toString()]
  ];

  inputData.forEach(([label, value]) => {
    doc.text(`${label}: ${value}`, margin, yPosition);
    yPosition += 8;
  });

  yPosition += 10;

  // Results Section
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text(t("common.results"), margin, yPosition);
  yPosition += 15;

  doc.setFontSize(12);
  doc.setFont("helvetica", "normal");

  const resultData = [
    [t("tools.compound.initialInvestment"), formatCurrency(data.initialInvestment)],
    [t("tools.compound.contributions"), formatCurrency(data.totalContributions)],
    [t("tools.compound.interestEarned"), formatCurrency(data.interestEarned)],
    [t("tools.compound.finalBalance"), formatCurrency(data.finalBalance)]
  ];

  resultData.forEach(([label, value]) => {
    doc.text(`${label}: ${value}`, margin, yPosition);
    yPosition += 8;
  });

  yPosition += 15;

  // Yearly Breakdown Table
  if (data.yearlyData.length > 0) {
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text(t("tools.compound.year") + " " + t("common.results"), margin, yPosition);
    yPosition += 10;

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");

    // Table headers
    const headers = [
      t("tools.compound.year"),
      t("tools.compound.contributions"),
      t("tools.compound.totalBalance")
    ];

    const colWidths = [30, 60, 60];
    let xPosition = margin;

    doc.setFont("helvetica", "bold");
    headers.forEach((header, index) => {
      doc.text(header, xPosition, yPosition);
      xPosition += colWidths[index];
    });
    yPosition += 8;

    // Table data
    doc.setFont("helvetica", "normal");
    data.yearlyData.slice(0, 20).forEach((item) => { // Limit to first 20 years for space
      xPosition = margin;
      const rowData = [
        item.year.toString(),
        formatCurrency(item.principal),
        formatCurrency(item.balance)
      ];

      rowData.forEach((cell, index) => {
        doc.text(cell, xPosition, yPosition);
        xPosition += colWidths[index];
      });
      yPosition += 6;

      // Add new page if needed
      if (yPosition > 270) {
        doc.addPage();
        yPosition = 30;
      }
    });
  }

  // Footer
  const currentDate = new Date().toLocaleDateString();
  doc.setFontSize(8);
  doc.setFont("helvetica", "italic");
  doc.text(`Generated on ${currentDate} by Finanzly`, pageWidth / 2, 285, { align: "center" });

  return doc;
};
